import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import subscriptionAPI from '../api/subscriptionAPI';
import LoadingSpinner from '../components/LoadingSpinner';
import './HelperSubscriptions.css';

const HelperSubscriptions = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    if (user && user._id) {
      fetchHelperSubscriptions();
    }
  }, [filter, user]);

  const fetchHelperSubscriptions = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await subscriptionAPI.getHelperSubscriptions(user._id, params);
      
      if (response.success) {
        setSubscriptions(response.subscriptions);
        setPagination(response.pagination || {});
      } else {
        setError(response.message || 'Failed to fetch subscriptions');
      }
    } catch (error) {
      console.error('Error fetching helper subscriptions:', error);
      setError('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSubscription = async (subscriptionId) => {
    try {
      if (!window.confirm('Are you sure you want to accept this subscription?')) {
        return;
      }

      const response = await subscriptionAPI.acceptSubscription(subscriptionId);

      if (response.success) {
        alert('Subscription accepted successfully!');
        fetchHelperSubscriptions();
      } else {
        alert(response.message || 'Failed to accept subscription');
      }
    } catch (error) {
      console.error('Error accepting subscription:', error);
      alert('Failed to accept subscription');
    }
  };

  const handleDeclineSubscription = async (subscriptionId) => {
    try {
      const reason = prompt('Please provide a reason for declining:');
      if (!reason) return;

      if (!window.confirm('Are you sure you want to decline this subscription?')) {
        return;
      }

      const response = await subscriptionAPI.declineSubscription(subscriptionId, { reason });

      if (response.success) {
        alert('Subscription declined');
        fetchHelperSubscriptions();
      } else {
        alert(response.message || 'Failed to decline subscription');
      }
    } catch (error) {
      console.error('Error declining subscription:', error);
      alert('Failed to decline subscription');
    }
  };

  const handleMarkServiceComplete = async (subscriptionId, serviceDate) => {
    try {
      const response = await subscriptionAPI.markServiceComplete(subscriptionId, { serviceDate });

      if (response.success) {
        alert('Service marked as completed!');
        fetchHelperSubscriptions();
      } else {
        alert(response.message || 'Failed to mark service as complete');
      }
    } catch (error) {
      console.error('Error marking service complete:', error);
      alert('Failed to mark service as complete');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'pending': return '#ffc107';
      case 'paused': return '#fd7e14';
      case 'cancelled': return '#dc3545';
      case 'completed': return '#6c757d';
      case 'expired': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getNextServiceDate = (subscription) => {
    // Calculate next service date based on schedule
    const today = new Date();
    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = daysOfWeek[today.getDay()];
    
    // Find next scheduled day
    const subscribedDays = subscription.schedule.daysOfWeek;
    const currentDayIndex = daysOfWeek.indexOf(currentDay);
    
    for (let i = 0; i < 7; i++) {
      const checkDay = (currentDayIndex + i) % 7;
      const dayName = daysOfWeek[checkDay];
      
      if (subscribedDays.includes(dayName)) {
        const nextDate = new Date(today);
        nextDate.setDate(today.getDate() + i);
        return nextDate;
      }
    }
    
    return new Date(); // fallback
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="helper-subscriptions">
      <div className="container">
        <div className="header-section">
          <h1>My Subscription Jobs</h1>
          <p>Manage subscriptions assigned to you</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="filters">
          <button 
            className={filter === 'all' ? 'active' : ''} 
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={filter === 'pending' ? 'active' : ''} 
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button 
            className={filter === 'active' ? 'active' : ''} 
            onClick={() => setFilter('active')}
          >
            Active
          </button>
          <button 
            className={filter === 'paused' ? 'active' : ''} 
            onClick={() => setFilter('paused')}
          >
            Paused
          </button>
          <button 
            className={filter === 'completed' ? 'active' : ''} 
            onClick={() => setFilter('completed')}
          >
            Completed
          </button>
        </div>

        {subscriptions.length === 0 ? (
          <div className="no-subscriptions">
            <div className="empty-state">
              <h3>No subscription jobs found</h3>
              <p>You don't have any subscription assignments yet.</p>
            </div>
          </div>
        ) : (
          <div className="subscriptions-list">
            {subscriptions.map((subscription) => (
              <div key={subscription._id} className="subscription-card">
                <div className="subscription-header">
                  <div className="subscription-info">
                    <h3>Customer: {subscription.customerId?.name || 'Customer'}</h3>
                    <div className="subscription-meta">
                      <span 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(subscription.status) }}
                      >
                        {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                      </span>
                      <span className="subscription-id">#{subscription._id.slice(-6)}</span>
                    </div>
                  </div>
                  <div className="subscription-amount">
                    <div className="amount">₹{subscription.pricing.totalMonthlyAmount}</div>
                    <div className="period">per month</div>
                  </div>
                </div>

                <div className="subscription-details">
                  <div className="detail-row">
                    <strong>Services:</strong>
                    <div className="services-list">
                      {subscription.services.map((service, index) => (
                        <span key={index} className="service-tag">
                          {service.serviceType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          {service.serviceOptions?.apartmentType && 
                            ` (${service.serviceOptions.apartmentType.toUpperCase()})`
                          }
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="detail-row">
                    <strong>Schedule:</strong>
                    <div className="schedule-info">
                      <div className="days">
                        Days: {subscription.schedule.daysOfWeek.map(day => 
                          day.charAt(0).toUpperCase() + day.slice(1)
                        ).join(', ')}
                      </div>
                      <div className="time-slots">
                        {subscription.schedule.timeSlots.map((slot, index) => (
                          <span key={index} className="time-slot">
                            {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {subscription.status === 'active' && (
                    <div className="detail-row">
                      <strong>Next Service:</strong>
                      <span className="next-service-date">
                        {formatDate(getNextServiceDate(subscription))}
                      </span>
                    </div>
                  )}

                  <div className="detail-row">
                    <strong>Duration:</strong>
                    <span>
                      {formatDate(subscription.subscriptionPeriod.startDate)} - {formatDate(subscription.subscriptionPeriod.endDate)}
                    </span>
                  </div>

                  <div className="detail-row">
                    <strong>Progress:</strong>
                    <div className="progress-info">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${subscription.progressPercentage || 0}%` }}
                        ></div>
                      </div>
                      <span>{subscription.servicesCompleted || 0}/{subscription.totalServicesScheduled || 0} services completed</span>
                    </div>
                  </div>

                  <div className="detail-row">
                    <strong>Customer Address:</strong>
                    <span>{subscription.customerAddress}</span>
                  </div>

                  {subscription.specialRequests && (
                    <div className="detail-row">
                      <strong>Special Requests:</strong>
                      <span>{subscription.specialRequests}</span>
                    </div>
                  )}
                </div>

                <div className="subscription-actions">
                  {subscription.status === 'pending' && (
                    <>
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleAcceptSubscription(subscription._id)}
                      >
                        Accept Job
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={() => handleDeclineSubscription(subscription._id)}
                      >
                        Decline
                      </button>
                    </>
                  )}
                  
                  {subscription.status === 'active' && (
                    <>
                      <button 
                        className="btn btn-success"
                        onClick={() => handleMarkServiceComplete(subscription._id, new Date().toISOString().split('T')[0])}
                      >
                        Mark Today's Service Complete
                      </button>
                      <a 
                        href={`tel:${subscription.customerId?.phone || ''}`}
                        className="btn btn-secondary"
                      >
                        📞 Call Customer
                      </a>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.pages > 1 && (
          <div className="pagination">
            <span>Page {pagination.page} of {pagination.pages}</span>
            <span>{pagination.total} total subscriptions</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelperSubscriptions; 